import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Typography, Select, Tooltip, Popover, Button, Divider } from 'antd';
import {
    TagsOutlined,
    CopyOutlined,
    CheckOutlined,
    SettingOutlined,
    CheckSquareOutlined,
    CloseOutlined,
    ExpandOutlined,
    CompressOutlined
} from '@ant-design/icons';
import categoryService from '@/services/category.service';

const FloatingToolbox = ({
    isEditMode,
    selectedCategory,
    setSelectedCategory,
    handleTextDragStart,
    handleTextDragEnd
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [categories, setCategories] = useState(null);
    const toolboxRef = useRef(null);
const categorycall = useRef(false);

useEffect(() => {
  if (categorycall.current) return; // prevent double call in StrictMode
  categorycall.current = true;

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllcategory();
      setCategories(response.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  fetchCategories();
}, []);

    // Generate textCategories from API data
    const textCategories = useMemo(() => {
        if (!categories || !Array.isArray(categories)) {
            return {};
        }

        const dynamicCategories = {};
        const colors = ['#52c41a', '#1890ff', '#722ed1', '#f5222d', '#fa8c16', '#13c2c2', '#eb2f96', '#faad14'];
        const icons = [CheckOutlined, SettingOutlined, CheckSquareOutlined, TagsOutlined];

        categories.forEach((cat, index) => {
            dynamicCategories[cat.category_key] = {
                name: cat.category_key.charAt(0).toUpperCase() + cat.category_key.slice(1),
                icon: icons[index % icons.length],
                color: colors[index % colors.length],
                items: cat.category_items || []
            };
        });

        return dynamicCategories;
    }, [categories]);

    // Set default category when categories load
    useEffect(() => {
        if (Object.keys(textCategories).length > 0 && !selectedCategory) {
            const firstCategoryKey = Object.keys(textCategories)[0];
            setSelectedCategory(firstCategoryKey);
        }
    }, [textCategories, selectedCategory, setSelectedCategory]);

    const handleDragStart = (e, text) => {
        setIsDragging(true);
        handleTextDragStart(e, text);
    };

    const handleDragEnd = (e) => {
        setIsDragging(false);
        handleTextDragEnd(e);
    };

    const PopoverContent = () => (
        <div className="w-64 max-h-80 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                    <TagsOutlined className="text-green-500" />
                    <Typography.Text strong className="text-sm">Text Library</Typography.Text>
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => setPopoverVisible(false)}
                />
            </div>

            {/* Category Selector */}
            <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                size="small"
                className="w-full mb-3"
                placeholder="Select category"
            >
                {Object.entries(textCategories).map(([key, category]) => (
                    <Select.Option key={key} value={key}>
                        <div className="flex items-center">
                            <div
                                className="w-2 h-2 rounded-full mr-2"
                                style={{ backgroundColor: category.color }}
                            />
                            {category.name}
                        </div>
                    </Select.Option>
                ))}
            </Select>

            <Divider style={{ margin: '8px 0' }} />

            {/* Compact Items Grid */}
            <div className="flex-1 overflow-y-auto">
                {textCategories[selectedCategory]?.items?.length > 0 ? (
                    <div className="grid grid-cols-2 gap-1">
                        {textCategories[selectedCategory].items.map((text, index) => (
                            <Tooltip key={index} title="Drag to form component" placement="top">
                                <div
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, text)}
                                    onDragEnd={handleDragEnd}
                                    className={`p-2 border rounded text-xs cursor-move hover:shadow-sm transition-all ${isDragging ? 'opacity-50' : 'hover:bg-green-50 hover:border-green-300'
                                        }`}
                                    style={{ borderColor: `${textCategories[selectedCategory]?.color}40` }}
                                >
                                    <div className="flex items-center space-x-1">
                                        <div
                                            className="w-1 h-1 rounded-full flex-shrink-0"
                                            style={{ backgroundColor: textCategories[selectedCategory]?.color }}
                                        />
                                        <span className="truncate text-xs">{text}</span>
                                    </div>
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <Typography.Text type="secondary" className="text-xs">
                            {categories === null ? 'Loading...' : 'No items available'}
                        </Typography.Text>
                    </div>
                )}
            </div>
        </div>
    );

    if (!isEditMode) return null;

    return (
        <>
            <Popover
                content={<PopoverContent />}
                title={null}
                trigger="click"
                open={popoverVisible}
                onOpenChange={setPopoverVisible}
                placement="topRight"
                overlayClassName="compact-toolbox-popover"
            >
                <div className="fixed right-5 bottom-16 z-50">
                    <div className="w-10 h-10 flash-bgcolor text-white rounded-full shadow-lg cursor-pointer hover:scale-105 transition-all flex items-center justify-center">
                        <TagsOutlined className="text-white text-lg" />
                    </div>
                </div>
            </Popover>

            <style jsx global>{`
        /* Compact Toolbox Styles */
        .compact-toolbox-popover .ant-popover-content {
          padding: 0;
        }
        
        .compact-toolbox-popover .ant-popover-inner {
          padding: 12px;
          border-radius: 8px;
        }

        /* Smooth animations */
        .transition-all {
          transition: all 0.2s ease;
        }

        /* Custom scrollbar for compact view */
        .overflow-y-auto::-webkit-scrollbar {
          width: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 2px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .fixed.right-4.bottom-20 {
            right: 8px;
            bottom: 80px;
          }
          
          .w-72 {
            width: calc(100vw - 32px);
            max-width: 288px;
          }
        }

        /* Drag feedback */
        .cursor-move:active {
          transform: scale(0.98);
          opacity: 0.8;
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .transition-all,
          .transition-transform,
          .transition-colors {
            transition: none !important;
          }
          
          .hover\\:scale-105:hover {
            transform: none !important;
          }
        }
      `}</style>
        </>
    );
};

export default FloatingToolbox;